import React, { useState, useEffect, useRef } from 'react';
import { withStyles, Modal } from '@material-ui/core';
import { refazerTexto } from '../../services/TextoService';
import Button from '../Button/Button';


const ModalGeracaoAudio = ({ classes, onSalvarTexto }) => {
  const [open, setOpen] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState('Dispositivo padrão: ...');
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const finalTranscript = useRef('');
  
  const canvasRef = useRef(null);
  const recognitionRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const animationIdRef = useRef(null);

  // Carrega e exibe o nome do microfone padrão
  const loadDefaultMicrophone = async () => {
    try {
      const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const track = tempStream.getAudioTracks()[0];
      const label = track.label || 'Desconhecido';
      track.stop();
      setDeviceInfo('Dispositivo padrão: ' + label);
    } catch (err) {
      setDeviceInfo('Permissão de microfone negada.');
      console.error(err);
    }
  };

  // Configura o visualizador de forma de onda
  const setupVisualizer = (stream) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasCtx = canvas.getContext('2d');
    
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const src = audioCtxRef.current.createMediaStreamSource(stream);
    const analyser = audioCtxRef.current.createAnalyser();
    src.connect(analyser);
    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationIdRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);
      
      canvasCtx.fillStyle = '#000';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);
      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = '#0f0';
      canvasCtx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        if (i === 0) canvasCtx.moveTo(x, y);
        else canvasCtx.lineTo(x, y);
        x += sliceWidth;
      }
      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();
    };
    draw();
  };

  // Configura o reconhecimento de fala
  const setupSpeechRecognition = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setTranscription('Web Speech API não suportada.');
      return;
    }
    const recognition = new SR();
    recognition.lang = 'pt-BR';
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        if (res.isFinal) {
          finalTranscript.current += res[0].transcript.trim() + '\n';
        } else {
          interim = res[0].transcript;
        }
      }
      setTranscription(finalTranscript.current + interim);
    };

    recognition.onerror = (e) => console.error('SpeechRecognition erro:', e);
    recognitionRef.current = recognition;
  };

  // Inicia ou para a gravação
  const toggleRecording = async () => {
    if (!isRecording) {
      finalTranscript.current = '';
      setTranscription('');
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        setupVisualizer(stream);
        setupSpeechRecognition();
        if (recognitionRef.current) {
          recognitionRef.current.start();
        }
      } catch (err) {
        console.error('Erro ao acessar o microfone:', err);
        return;
      }
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
      }
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      
      // Chama o endpoint para refazer o texto
      if (finalTranscript.current.trim()) {
        try {
          const response = await refazerTexto(finalTranscript.current);
          const textoRefeito = response.data.refazerTexto.textoRefeito;
          setTranscription(textoRefeito);
        } catch (error) {
          console.error('Erro ao refazer texto:', error);
        }
      }
    }
    setIsRecording(!isRecording);
  };

  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', loadDefaultMicrophone);
    return () => {
      // Limpeza ao desmontar o componente
      navigator.mediaDevices.removeEventListener('devicechange', loadDefaultMicrophone);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
      }
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []);

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <div className={classes.modalContent}>
          <div className={classes.audioRecorderContainer}>
            <h1>Gravador de Áudio</h1>
            <div className={classes.deviceInfo}>{deviceInfo}</div>
            <Button id="prt-mga-btn-grv" onClick={toggleRecording} disabled={deviceInfo.includes('negada')}>
              {isRecording ? 'Parar Gravação' : 'Iniciar Gravação'}
            </Button>
            <canvas ref={canvasRef} className={classes.visualizer}></canvas>
            <h2>Transcrição</h2>
            <div className={classes.transcription}>{transcription}</div>
            <div className={classes.buttonGroup}>
              <Button id="prt-mga-btn-slv" onClick={() => onSalvarTexto && onSalvarTexto(transcription)} disabled={!transcription.trim()}>Salvar no Prontuário</Button>
              <Button id="prt-mga-btn-fch" onClick={() => setOpen(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const styles = {
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    outline: 'none'
  },
  audioRecorderContainer: {
    fontFamily: 'Arial, sans-serif',
    padding: '20px',
    background: '#f9f9f9'
  },
  deviceInfo: {
    fontWeight: 'bold',
    marginBottom: '1rem'
  },
  button: {
    fontSize: '1rem',
    padding: '0.5rem 1rem',
    margin: '0.5rem 0'
  },
  transcription: {
    marginTop: '1rem',
    padding: '1rem',
    height: '150px',
    border: '1px solid #ccc',
    background: '#fff',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap'
  },
  visualizer: {
    width: '100%',
    height: '100px',
    background: '#000',
    marginTop: '1rem',
    display: 'block'
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginTop: '1rem'
  }
};

export default withStyles(styles)(ModalGeracaoAudio);